/**
 * useSprayWalls — the gym's spray walls, live.
 *
 * One `onSnapshot` on `gyms/{gymId}/sprayWalls`. Three deliberate choices:
 *
 * - No `active` filter. The Spraywall tab reuses this list and wall managers
 *   need the hidden walls too; visibility is decided client-side
 *   (`isWallVisibleOnMap` for the map). A gym has a handful of walls, so the
 *   read is cheap.
 * - Add-on gated. A gym without the add-on never subscribes (performance
 *   guide: no listener for a feature the gym does not have). Switching the
 *   add-on off unsubscribes and clears the list.
 * - Not tab-gated. The map is the default tab and draws the markers, so the
 *   list must be live from the first render; there are few docs. The
 *   Spraywall tab reads this same list — never a second listener.
 *
 * `sprayWallsLoaded` is true only after the first snapshot of the CURRENT
 * subscription (false while disabled and again on every resubscribe). The
 * tab needs it to tell "no published wall" from "not loaded yet": bouncing on
 * the empty pre-snapshot list would throw every `#spraywall…` deep link back
 * to the map (plan §9.1, the "wait" outcome).
 */
import { useState, useEffect } from "react";
import { onSnapshot } from "firebase/firestore";
import { useListenerRetry } from "./useListenerRetry";

function sortWalls(walls) {
  return walls.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)
    || (a.name || "").localeCompare(b.name || ""));
}

export function useSprayWalls({ gymId, gymCollection, enabled }) {
  const [sprayWalls, setSprayWalls] = useState([]);
  const [sprayWallsLoaded, setSprayWallsLoaded] = useState(false);
  const { nonce, onError, markAlive } = useListenerRetry("sprayWalls", gymId || "");

  useEffect(() => {
    setSprayWallsLoaded(false);
    if (!enabled || !gymId || !gymCollection) {
      setSprayWalls([]);
      return undefined;
    }
    return onSnapshot(gymCollection("sprayWalls"), (snap) => {
      markAlive();
      setSprayWalls(sortWalls(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
      setSprayWallsLoaded(true);
    }, onError("sprayWalls"));
  }, [gymId, enabled, gymCollection, nonce, onError, markAlive]);

  return { sprayWalls, sprayWallsLoaded };
}
